import { useEffect, useState } from 'react';
import type { AircraftDetail, AircraftFeature } from '@tsr/shared-types';
import { fetchAircraftDetail } from './api';
import { classifyAircraft, roleColor, roleLabel } from './aircraftStyles';

/** Live-updating "last seen N s/min ago" counter from an ISO timestamp. */
function useSecondsSince(iso: string | null): number | null {
  const [secs, setSecs] = useState<number | null>(() =>
    iso ? Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000)) : null,
  );
  useEffect(() => {
    if (!iso) {
      setSecs(null);
      return;
    }
    const tick = (): void => setSecs(Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000)));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [iso]);
  return secs;
}

function formatAgo(secs: number | null): string {
  if (secs === null) return 'never';
  if (secs < 90) return `${secs}s ago`;
  if (secs < 90 * 60) return `${Math.round(secs / 60)} min ago`;
  if (secs < 36 * 3600) return `${Math.round(secs / 3600)}h ago`;
  return `${Math.round(secs / 86400)}d ago`;
}

function StatusTag({ status }: { status: string }): JSX.Element {
  const colors: Record<string, string> = {
    live: 'bg-emerald-500/20 text-emerald-300',
    stale: 'bg-amber-500/20 text-amber-300',
    based: 'bg-indigo-500/15 text-indigo-300',
    undiscovered: 'bg-slate-700 text-slate-300',
  };
  return (
    <span className={`inline-block text-[10px] px-1.5 py-0.5 rounded ${colors[status] ?? 'bg-slate-700 text-slate-300'}`}>
      {status === 'based' ? 'last known' : status}
    </span>
  );
}

/**
 * Side-panel detail for a live aircraft (R.1b §3.4). Renders the list feature
 * immediately, then enriches with the detail payload (base, operator asset,
 * full metadata). Surfaces a transparency cue when the ICAO hex was discovered
 * from a live transmission (CAF watchlist aircraft).
 */
export function AircraftMedia({ aircraft }: { aircraft: AircraftFeature }): JSX.Element {
  const [detail, setDetail] = useState<AircraftDetail | null>(null);
  const p = aircraft.properties;
  const style = classifyAircraft(p.aircraft_role, p.aircraft_type, p.aircraft_type);
  const secs = useSecondsSince(p.last_seen_at);

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    void fetchAircraftDetail(aircraft.id)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [aircraft.id]);

  const dp = detail?.properties;
  const adsbxUrl = p.icao_hex
    ? `https://globe.adsbexchange.com/?icao=${p.icao_hex.toLowerCase()}`
    : null;

  return (
    <div className="px-4 py-4 space-y-4" data-testid="aircraft-media">
      <div className="border-l-2 pl-3" style={{ borderColor: roleColor(p.aircraft_role) }}>
        <div className="flex items-center gap-2">
          <span
            className="inline-block w-3 h-3 rounded-full"
            style={{ backgroundColor: roleColor(p.aircraft_role) }}
            aria-hidden
          />
          <h2 className="text-lg font-mono font-semibold flex-1 tracking-wide">{p.tail_number}</h2>
          <StatusTag status={p.tracking_status} />
        </div>
        <p className="text-xs text-slate-400 mt-1">
          <span
            className="inline-block px-1.5 py-0.5 rounded text-[11px]"
            style={{ backgroundColor: `${roleColor(p.aircraft_role)}22`, color: roleColor(p.aircraft_role) }}
          >
            {roleLabel(p.aircraft_role)}
          </span>
          <span className="ml-2">{p.aircraft_type}</span>
          <span className="ml-1 text-slate-500">· {style.kind}</span>
        </p>
      </div>

      {p.tracking_status === 'based' && (
        <div
          data-testid="based-caveat"
          className="rounded border border-indigo-500/30 bg-indigo-500/10 px-3 py-2 text-xs text-indigo-200"
        >
          <span className="font-semibold">Last seen here · {formatAgo(secs)}</span> — last known
          position from the final ADS-B fix, not a verified base. The airframe is likely parked or
          based nearby; the marker returns to live on its next transmission.
        </div>
      )}

      <section>
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-2">
          {p.tracking_status === 'live' ? 'Live telemetry' : 'Last fix'}
        </h3>
        <dl className="grid grid-cols-2 gap-y-1 text-sm">
          <dt className="text-slate-400">Altitude</dt>
          <dd className="font-mono">
            {p.altitude_m !== null ? `${Math.round(p.altitude_m).toLocaleString()} m` : '—'}
          </dd>
          <dt className="text-slate-400">Speed</dt>
          <dd className="font-mono">{p.speed_kt !== null ? `${Math.round(p.speed_kt)} kt` : '—'}</dd>
          <dt className="text-slate-400">Heading</dt>
          <dd className="font-mono">{p.heading_deg !== null ? `${Math.round(p.heading_deg)}°` : '—'}</dd>
          <dt className="text-slate-400">Last seen</dt>
          <dd className="font-mono" data-testid="last-seen-counter">
            {formatAgo(secs)}
          </dd>
        </dl>
      </section>

      <section>
        <h3 className="text-xs uppercase tracking-wider text-slate-400 mb-2">Aircraft</h3>
        <dl className="grid grid-cols-2 gap-y-1 text-sm">
          <dt className="text-slate-400">ICAO hex</dt>
          <dd className="font-mono">{p.icao_hex ?? '—'}</dd>
          {p.operator_org && (
            <>
              <dt className="text-slate-400">Operator</dt>
              <dd className="font-mono">{p.operator_org}</dd>
            </>
          )}
          {dp?.operator_asset_name && (
            <>
              <dt className="text-slate-400">Base / resource</dt>
              <dd className="text-slate-200">{dp.operator_asset_name}</dd>
            </>
          )}
          {dp?.engine_count != null && (
            <>
              <dt className="text-slate-400">Engines</dt>
              <dd className="font-mono">{dp.engine_count}</dd>
            </>
          )}
        </dl>
      </section>

      {p.hex_captured_from_live && (
        <div
          data-testid="hex-discovered-note"
          className="rounded border border-sky-500/40 bg-sky-500/10 px-3 py-2 text-xs text-sky-200"
        >
          <span className="font-semibold">ICAO hex discovered via live ADS-B</span> — military hex
          codes for this aircraft are not publicly published; it was captured on first live transmission.
        </div>
      )}

      {adsbxUrl && (
        <a
          href={adsbxUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="block text-center text-sm py-1.5 rounded bg-slate-800 hover:bg-slate-700 border border-slate-700 text-sky-300"
        >
          Open on ADS-B Exchange ↗
        </a>
      )}
    </div>
  );
}
